import type { BrandSpecialQualifyingStatus } from '@iheartjane/data-access';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import sumBy from 'lodash-es/sumBy';

/* eslint-disable-next-line no-restricted-imports */
import type { BrandSpecial } from '@jane/shared/models';

interface UseStatusText {
  currentSpecial?: BrandSpecial;
  qualifyingStatus?: BrandSpecialQualifyingStatus | null;
  requiredEligibility: boolean;
}

export const useStatusText = ({
  qualifyingStatus,
  currentSpecial,
  requiredEligibility,
}: UseStatusText) => {
  const isApplied = qualifyingStatus?.qualifying_status === 'applied';
  const isQualified = qualifyingStatus?.qualifying_status === 'qualified';
  const isUnlocked = isApplied || isQualified;
  const isProductType = currentSpecial?.special_type === 'product';

  const remainingQualifiers =
    (currentSpecial?.rules?.settings?.threshold_number_of_items_in_cart || 0) -
    sumBy(qualifyingStatus?.qualified_products, 'count');
  const remainingDiscountables =
    (currentSpecial?.rules?.settings?.max_number_of_discounted_products || 0) -
    sumBy(qualifyingStatus?.discounted_products, 'count');

  const reachedMaxDiscountables = remainingDiscountables === 0;
  const showQualifierText = remainingQualifiers > 0;
  const formattedDollarAmount = formatCurrency(
    currentSpecial?.discount_dollar_amount,
    true
  );
  const formattedTotalAmount = formatCurrency(
    qualifyingStatus?.discount_total_with_fee,
    true
  );

  const calloutText = showQualifierText
    ? `Add ${pluralize({
        noun: 'product',
        number: remainingQualifiers,
        returnValue: 'both',
      })} to unlock reward`
    : remainingDiscountables > 0
      ? `Add up to ${remainingDiscountables} more ${pluralize({
          noun: 'product',
          number: remainingDiscountables,
          returnValue: 'word',
        })}`
      : undefined;

  const cashBackPer =
    currentSpecial?.discount_type === 'dollar_amount'
      ? formattedDollarAmount
      : `${currentSpecial?.discount_percent}%`;

  const calloutSubtext =
    remainingQualifiers === 0 && remainingDiscountables > 0
      ? `${cashBackPer} cash back each`
      : undefined;

  const totalCashBack = `${formattedTotalAmount} total cash back`;

  const footerText = reachedMaxDiscountables
    ? 'You maxed out your reward!'
    : qualifyingStatus?.discount_total_with_fee &&
        qualifyingStatus?.discount_total_with_fee > 0
      ? totalCashBack
      : 'Reward unlocked!';

  const footerSubtext = reachedMaxDiscountables
    ? `${formattedTotalAmount} total cash back`
    : undefined;

  const stickyText =
    isProductType || !isUnlocked
      ? currentSpecial?.product_brand.name || ''
      : isApplied
        ? totalCashBack
        : 'Reward unlocked!';

  const stickySubtext =
    isProductType || !isUnlocked ? currentSpecial?.title : undefined;

  const productCalloutText = requiredEligibility
    ? 'Sign in for eligibility'
    : undefined;

  const stickyCallout = isProductType
    ? productCalloutText
    : reachedMaxDiscountables
      ? footerText
      : calloutText;

  return {
    calloutText,
    calloutSubtext,
    footerText,
    footerSubtext,
    stickyText,
    stickySubtext,
    stickyCallout,
    stickyCalloutSubtext: calloutSubtext && `(${calloutSubtext})`,
  };
};
